import * as React from "react";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useOne,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { Loader2 } from "lucide-react";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { AliasEditor } from "@/pages/areas/components/alias-editor";
import { AreaPreview } from "@/pages/areas/components/area-preview";
import { AreaVenuesPanel } from "@/pages/areas/components/area-venues-panel";
import { DisplayNamesEditor } from "@/pages/areas/components/display-names-editor";
import { FilterSelect } from "@/pages/areas/components/filter-select";
import { MergeAreaDialog } from "@/pages/areas/components/merge-area-dialog";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import { useReloadAreas } from "@/pages/areas/hooks/use-reload-areas";
import { buildAreaPatch } from "@/pages/areas/lib/area-patch";
import { canMarkReviewed } from "@/pages/areas/lib/can-mark-reviewed";
import { runBatches } from "@/pages/areas/lib/run-batches";
import {
  type AreaEditFormType,
  areaEditSchema,
} from "@/pages/areas/schemas/area.edit.form";
import type { Area, AreaVenueItem } from "@/types/area";

const schemaProvider = new ZodProvider(areaEditSchema);
const SYNC_BATCH = 50;

export const AreaEdit = () => {
  const { id } = useParsed();
  const repo = useAreaRepository();
  const reload = useReloadAreas();
  const { open: notify } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  const { result, query } = useOne<Area>({
    resource: "areas",
    dataProviderName: "areas",
    id: id?.toString() ?? "",
    queryOptions: { enabled: Boolean(id) },
  });
  const [area, setArea] = React.useState<Area>();
  const [core, setCore] = React.useState<AreaEditFormType>({
    name: "",
    keepOldNameAsAlias: true,
  });
  const [parentId, setParentId] = React.useState<string>();
  const [partOrder, setPartOrder] = React.useState<number>();
  const [displayNames, setDisplayNames] = React.useState<
    Record<string, string>
  >({});
  const [venues, setVenues] = React.useState<AreaVenueItem[]>([]);
  const [cityAreas, setCityAreas] = React.useState<Area[]>([]);
  // Part options, "city has parts" and the child check read the city's own documents, so they
  // wait for them rather than judge an empty list.
  const [cityLoaded, setCityLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [progress, setProgress] = React.useState<number>();
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string>();
  const [syncStalled, setSyncStalled] = React.useState(false);

  const adopt = (next: Area) => {
    setArea(next);
    setCore({ name: next.name, keepOldNameAsAlias: true });
    setParentId(next.parentId ?? undefined);
    setPartOrder(next.partOrder ?? undefined);
    setDisplayNames(next.displayNames ?? {});
  };

  React.useEffect(() => {
    setLoading(query.isLoading);
  }, [query.isLoading]);

  React.useEffect(() => {
    if (result && result.id !== area?.id) {
      adopt(result);
      setTitle(`Edit ${result.name}`);
    }
  }, [result]);

  React.useEffect(() => {
    if (!area) return;
    setLoadError(undefined);
    setCityLoaded(false);
    const failed = (what: string) => (error: unknown) => {
      console.error(`Could not load the ${what}`, error);
      setLoadError(toHumanErrorMessage(error));
    };
    repo.venues(area.id).then(setVenues).catch(failed("area venues"));
    repo
      .cityAreas(area.city, area.countryCode)
      .then((areas) => {
        setCityAreas(areas);
        setCityLoaded(true);
      })
      .catch(failed("city areas"));
  }, [area?.id]);

  const patch = area
    ? buildAreaPatch(area, { ...core, parentId, partOrder, displayNames })
    : null;
  React.useEffect(() => {
    setWarnWhen(patch !== null);
  }, [patch !== null]);

  const cityParts = cityAreas.filter((r) => r.kind === "part");
  // The server refuses a part that still has child areas.
  const hasChildren = area
    ? cityAreas.some((r) => r.parentId === area.id)
    : false;
  const nameOf = (areaId: string) =>
    cityAreas.find((r) => r.id === areaId)?.name;

  const fail = (message: string, error: unknown) =>
    notify?.({
      type: "error",
      message,
      description: toHumanErrorMessage(error),
    });

  /** The venues carry the area's name as text: rewrite them 50 at a time. True when none remain. */
  const syncVenues = async (areaId: string): Promise<boolean> => {
    try {
      await runBatches(
        async () => {
          const r = await repo.syncVenuesBatch(areaId, SYNC_BATCH);
          return { done: r.updated, remaining: r.remaining };
        },
        (done, remaining) => setProgress(done + remaining),
      );
      setSyncStalled(false);
      return true;
    } catch (error) {
      // The rename is saved; only some venues still show the old name. Each batch is
      // idempotent, so "Retry venue update" resumes where this stopped.
      setSyncStalled(true);
      fail("Area saved, but its venues are not all updated", error);
      return false;
    } finally {
      setProgress(undefined);
    }
  };

  const onSubmit = async (values: AreaEditFormType) => {
    if (!area) return;
    const body = buildAreaPatch(area, {
      ...values,
      parentId,
      partOrder,
      displayNames,
    });
    if (!body) {
      notify?.({ type: "success", message: "Nothing to save" });
      return;
    }
    setSaving(true);
    try {
      const saved = await repo.update(area.id, body);
      // Saved: show the stored state now, so a failed venue sync below cannot leave a form
      // that re-sends the rename.
      adopt(saved);
      setTitle(`Edit ${saved.name}`);
      reload();
      setWarnWhen(false);
      if (body.name && !(await syncVenues(saved.id))) return;
      notify?.({ type: "success", message: "Area saved" });
    } catch (error) {
      fail("Could not save the area", error);
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async () => {
    if (!area) return;
    try {
      const saved = await repo.update(area.id, { origin: "curator" });
      // Only the origin: adopting the whole row would remount the form and drop unsaved edits.
      setArea((current) =>
        current ? { ...current, origin: saved.origin } : current,
      );
      reload();
    } catch (error) {
      fail("Could not mark the area reviewed", error);
    }
  };

  if (!area) return null;

  return (
    <div className="flex flex-col gap-6 p-8 pb-20 lg:flex-row">
      <div className="flex flex-col gap-4 lg:w-1/2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            {area.city}, {area.countryCode}
          </span>
          <Badge variant="outline">{area.kind}</Badge>
          <Badge variant="secondary">{area.origin || "unknown origin"}</Badge>
          <Badge variant="outline">{area.venueCount} venues</Badge>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-xs text-muted-foreground">
          <dt>Centre</dt>
          <dd>
            {area.centre
              ? `${area.centre.latitude.toFixed(5)}, ${area.centre.longitude.toFixed(5)}`
              : "none yet"}
          </dd>
          <dt>Created</dt>
          <dd>{area.createdAt ?? "unknown"}</dd>
          <dt>Updated</dt>
          <dd>{area.updatedAt ?? "unknown"}</dd>
        </dl>
        <AutoForm
          key={`${area.id}:${area.name}:${area.updatedAt}`}
          schema={schemaProvider}
          defaultValues={core}
          onFormInit={(form) => {
            form.watch((v) =>
              setCore({
                name: v.name ?? "",
                keepOldNameAsAlias: v.keepOldNameAsAlias ?? true,
              }),
            );
          }}
          onSubmit={onSubmit}
        >
          {area.kind === "area" ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Part</span>
              <FilterSelect
                label="Part"
                value={parentId}
                placeholder="Choose a part"
                className="w-full"
                options={cityParts.map((p) => ({ value: p.id, label: p.name }))}
                onChange={setParentId}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <Label htmlFor="area-part-order">Part order</Label>
              <Input
                id="area-part-order"
                type="number"
                min={0}
                value={partOrder ?? ""}
                onChange={(e) =>
                  setPartOrder(
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
              />
            </div>
          )}
          <div className="mt-4 flex flex-col gap-2">
            <span className="text-sm font-medium">Display names</span>
            <DisplayNamesEditor
              value={displayNames}
              onChange={setDisplayNames}
            />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Apps pick up changes within 1 hour.
          </p>
          {syncStalled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void syncVenues(area.id)}
            >
              Retry venue update
            </Button>
          )}
          {progress !== undefined && (
            <p className="text-sm">Updating {progress} venues…</p>
          )}
          <BottomActionBar>
            <Button disabled={patch === null || saving} type="submit">
              {saving && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              Save
            </Button>
          </BottomActionBar>
        </AutoForm>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aliases</CardTitle>
          </CardHeader>
          <CardContent>
            <AliasEditor
              area={area}
              areaName={nameOf}
              onChange={(next) => {
                setArea(next);
                reload();
              }}
            />
          </CardContent>
        </Card>
        <div className="flex gap-2">
          {area.origin === "auto" && (
            <Button
              type="button"
              variant="outline"
              disabled={
                !cityLoaded || !canMarkReviewed(area, cityParts.length > 0)
              }
              onClick={markReviewed}
            >
              Mark reviewed
            </Button>
          )}
          {cityLoaded && !hasChildren && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setMergeOpen(true)}
            >
              Merge into another area
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:w-1/2">
        <AreaPreview area={area} cityAreas={cityAreas} venues={venues} />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Venues</CardTitle>
          </CardHeader>
          <CardContent>
            <AreaVenuesPanel venues={venues} error={loadError} />
          </CardContent>
        </Card>
      </div>
      <MergeAreaDialog
        source={area}
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        cityAreas={cityLoaded ? cityAreas : undefined}
      />
    </div>
  );
};
